/**
 * 서명 편집 다이얼로그 — 기본(회사 명함) / HTML 두 가지 방식.
 *
 *  initial:
 *    null/undefined  — 닫힘
 *    {}              — 새 서명 작성 모드
 *    { id, name, bodyType, card, bodyHtml, isDefault } — 기존 서명 수정 모드
 *
 * ## 왜 이렇게 바뀌었나 — "서명 수정 불가" 의 실체
 * 예전엔 편집 방식이 HTML 소스 직접 입력뿐이었다. 그런데 운영 서명 28건이 전부 450px 표 기반
 * 명함이라, 이름 한 글자를 고치려 해도 `<td width="234" style="border-bottom-width: 1px; …">`
 * 덩어리를 마주해야 했다. 그 결과 사람들은 수정 대신 <b>지우고 새로 만들기</b>를 반복했고
 * (운영 로그상 8회), "서명 수정이 안 된다"는 보고가 나왔다.
 * 서버 PUT 은 33회 정상 동작하고 있었다 — 고장난 건 API 가 아니라 이 화면이었다.
 *
 * ## 작성 방식 두 가지
 *   기본(BASIC) — 이름·부서·직급·연락처 <b>값</b>만 다룬다. 마크업은 보이지 않는다.
 *                 값의 초깃값은 서버가 "기존 서명 → 인사정보 → 회사 기본값" 순으로 조립해 준다.
 *                 대다수 직원은 이 모드만 쓰면 된다.
 *   HTML        — 공통 양식을 벗어난 대외용 서명. 마크업 직접 입력 + 이미지 삽입.
 *
 * ## 값을 입력칸으로 노출하는 이유 (전부 자동으로 못 채우는 이유)
 * 인사정보를 그대로 덮으면 <b>지금 쓰는 서명이 틀린 정보로 바뀐다.</b> 운영 24건 대조 결과:
 *   - 부서 표기가 인사 DB 와 다름 — 재무인사팀→경영지원본부, 영상팀→컨텐츠제작팀, 사내이사→운영본부
 *   - 직위(과장)가 아니라 <b>직책</b>(팀장·디자이너·감독)을 쓰는 사람이 많음
 *   - 직통번호(T.)는 인사 DB 에 컬럼 자체가 없음 — 사람마다 내선이 다름
 * 그래서 서버가 값을 미리 채워 주되(대개 손댈 필요 없음), 어긋난 칸만 고칠 수 있게 열어 둔다.
 * 인사정보 원본은 칩으로 옆에 띄워 한 번에 바꿔 넣을 수 있다.
 *
 * ## 미리보기는 서버 렌더 결과다
 * 명함 템플릿은 백엔드 MailSignatureCardTemplate 한 곳에만 있다. 프런트에 복제하면 언젠가
 * 한쪽만 고쳐져 "미리보기와 실제 나간 서명이 다른" 상태가 되는데, 서명은 외부로 나가는
 * 물건이라 그 어긋남을 발견하는 시점이 대개 사고 뒤다. 그래서 입력할 때마다(디바운스)
 * 서버에 렌더를 요청한다.
 *
 * ## 서버가 최종 결정권을 가진다
 *   BASIC 은 서버가 card 값으로 HTML 을 다시 만든다 (요청의 bodyHtml 은 무시).
 *   HTML 은 저장 시 새니타이즈되므로, 저장 후 다시 열면 정제된 소스가 보인다 — 의도된 동작.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Button, Chip, FormControlLabel, Stack, Switch, TextField,
    ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import ImageRoundedIcon   from "@mui/icons-material/ImageRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DOMPurify from "dompurify";

import { mailApi } from "../../api/mailApi";
import { notify }  from "../../../../util/toast";
import { confirm } from "../../../../util/confirmDialog";
import FormModal   from "../../../../component/common/FormModal";
import { pinImageBoxes } from "./pinImageBoxes";

const HTML_PLACEHOLDER = '<div style="font-size:13px; color:#334155;">\n  <b>OOO</b> / 주임<br/>\n  Onnme Inc.\n</div>';

/** 명함 값의 빈 한 벌 — 서버 응답이 오기 전 폼이 uncontrolled 로 시작하지 않게. */
const EMPTY_CARD = { name: "", dept: "", position: "", officePhone: "", mobile: "", email: "" };

/** 미리보기 렌더 요청 간격(ms). 타이핑마다 때리지 않되, 손을 멈추면 곧바로 보이는 정도. */
const PREVIEW_DEBOUNCE_MS = 400;

export default function SignatureEditorDialog({ open, onClose, initial, onSaved }) {
    const [name, setName]           = useState("");
    const [bodyType, setBodyType]   = useState("BASIC");
    const [bodyHtml, setBodyHtml]   = useState("");
    const [isDefault, setIsDefault] = useState(false);
    const [saving, setSaving]       = useState(false);
    const [uploading, setUploading] = useState(false);

    // 기본(명함) 모드
    const [card, setCard]               = useState(EMPTY_CARD);
    const [hr, setHr]                   = useState({});    // 인사정보 원본 — 칩으로 제시
    const [cardPreview, setCardPreview] = useState("");
    const [loadingCard, setLoadingCard] = useState(false);

    const htmlFieldRef = useRef(null);   // <img> 태그를 커서 위치에 끼워 넣기 위해
    const fileInputRef = useRef(null);

    const isEdit = Boolean(initial?.id);
    const isHtml = bodyType === "HTML";

    /**
     * 인사정보 + 기존 서명에서 값을 끌어온다.
     *
     * 실패해도 편집을 막지 않는다 — 빈 폼으로라도 열려야 한다. 여기서 예외를 던지면
     * 인사정보가 없는 계정(외부 협력사 등)은 서명을 아예 못 만든다.
     */
    const loadCardDefaults = useCallback(async (signatureId, { silent = false } = {}) => {
        setLoadingCard(true);
        try {
            const res = await mailApi.signatureCardDefaults(signatureId);
            const d = res?.data ?? res ?? {};
            setCard({ ...EMPTY_CARD, ...(d.card || {}) });
            setHr({ dept: d.hrDept, position: d.hrPosition, duty: d.hrDuty });
            if (!silent && d.fromExistingSignature) notify("기존 서명에서 값을 불러왔습니다", "info");
        } catch (e) {
            if (!silent) notify(e?.response?.data?.message || "인사정보를 불러오지 못했습니다", "warning");
        } finally { setLoadingCard(false); }
    }, []);

    useEffect(() => {
        if (!open) return;
        setName(initial?.name || "");
        setBodyHtml(initial?.bodyHtml || "");
        setIsDefault(Boolean(initial?.isDefault));
        setCardPreview("");

        // 어느 모드로 열지 —
        //   신규            → 기본 (대다수가 회사 명함을 쓴다)
        //   BASIC 로 저장됨 → 기본
        //   card 가 딸려옴  → 기본 (기존 HTML 서명이지만 서버가 명함 모양으로 역파싱에 성공한 경우.
        //                     이 기능 도입 전에 만들어진 28건이 여기 해당한다)
        //   그 외           → HTML (명함 틀을 벗어난 서명은 값으로 표현할 수 없다)
        const looksLikeCard = initial?.bodyType === "BASIC" || Boolean(initial?.card);
        const nextType = !initial?.id ? "BASIC" : (looksLikeCard ? "BASIC" : "HTML");
        setBodyType(nextType);

        if (nextType === "BASIC") {
            if (initial?.card) {
                setCard({ ...EMPTY_CARD, ...initial.card });
                // 인사정보 칩만 따로 받아 온다(값은 이미 있으므로 덮지 않는다).
                mailApi.signatureCardDefaults(initial?.id)
                    .then(r => { const d = r?.data ?? r ?? {}; setHr({ dept: d.hrDept, position: d.hrPosition, duty: d.hrDuty }); })
                    .catch(() => { /* 칩은 없어도 편집에 지장 없음 */ });
            } else {
                loadCardDefaults(initial?.id, { silent: true });
            }
        }
    }, [open, initial, loadCardDefaults]);

    /**
     * 기본 모드 미리보기 — 값이 바뀔 때마다 서버 렌더를 받아 온다.
     *
     * 이전 요청이 늦게 도착해 최신 미리보기를 덮어쓰는 걸 막으려고 `alive` 로 끊는다
     * (디바운스가 있어도 네트워크 순서까지 보장되지는 않는다).
     */
    useEffect(() => {
        if (!open || isHtml) return;
        let alive = true;
        const t = setTimeout(async () => {
            try {
                const res = await mailApi.signatureCardPreview(card);
                const html = (res?.data ?? res)?.html || "";
                if (alive) setCardPreview(html);
            } catch { if (alive) setCardPreview(""); }
        }, PREVIEW_DEBOUNCE_MS);
        return () => { alive = false; clearTimeout(t); };
    }, [open, isHtml, card]);

    const setField = (key) => (e) => setCard(c => ({ ...c, [key]: e.target.value }));

    /** HTML 모드 미리보기 — 저장 후 실제로 박힐 HTML 과 같은 것을 보여 준다. */
    const htmlPreview = useMemo(
        () => DOMPurify.sanitize(pinImageBoxes(bodyHtml) || "", { ADD_ATTR: ["target", "rel"] }),
        [bodyHtml],
    );

    /**
     * 방식 전환. 되돌릴 수 없는 손실이 있을 때만 확인을 받는다.
     *
     * 기본 → HTML : 지금 미리보기에 보이는 명함 HTML 을 소스 칸으로 옮겨 준다. 거기서
     *               마크업을 직접 손보는 것이 자연스러운 흐름이고, 빈 칸을 주면 처음부터 짜야 한다.
     * HTML → 기본 : 손으로 짠 마크업이 회사 템플릿으로 대체된다. 명함 모양이면 값을 뽑아
     *               채워 주지만(서버 역파싱), 아니면 값이 비어 시작한다.
     */
    const handleChangeType = async (next) => {
        if (!next || next === bodyType) return;

        if (next === "HTML") {
            if (!bodyHtml.trim() && cardPreview) setBodyHtml(cardPreview);
            setBodyType("HTML");
            return;
        }

        if (bodyHtml.trim()) {
            const ok = await confirm({
                title: "기본 방식으로 전환",
                message: "직접 작성한 HTML 대신 회사 표준 명함 서명이 들어갑니다.\n"
                       + "명함 형식이면 이름·연락처는 그대로 옮겨집니다. 계속할까요?",
                confirmLabel: "전환",
                variant: "danger",
            });
            if (!ok) return;
        }
        setBodyType("BASIC");
        await loadCardDefaults(initial?.id, { silent: true });
    };

    /**
     * 로고 등 이미지 넣기 → base64 data: URL 로 커서 위치에 <img> 삽입. (HTML 모드 전용)
     *
     * <h4>왜 S3 업로드(mailApi.uploadSignatureImage)를 쓰지 않나</h4>
     * 업로드가 돌려주는 건 `/api/mail/signatures/images/{key}` 상대 URL인데,
     * 백엔드 HtmlSanitizer(Jsoup)는 base URI 없이 clean 하므로 <b>상대 URL src 를 통째로 지운다</b>.
     * 저장 버튼을 누르는 순간 이미지가 사라진다는 뜻. 살아남는 건 http(s)/data/cid 뿐이다.
     *
     * data: 로 박아 두면 이후 흐름이 전부 자동이다 —
     * 작성기에서 즉시 렌더되고, 발송 직전 백엔드 extractInlineImages 가 cid: 인라인 첨부로 바꾼다.
     *
     * <h4>용량 제한을 두지 않는 이유</h4>
     * body_html 은 PostgreSQL text — 길이 제한이 없다(값당 1GB, TOAST 압축).
     * 예전 주석이 "MySQL TEXT(64KB)" 라고 적어 40KB 상한을 걸어 두었는데, 이 프로젝트의 DB 는
     * PostgreSQL 이라 근거가 없는 제한이었다(운영 서명 최대 5,923자). 다만 base64 이미지는
     * 발송 메일 크기를 그대로 키우므로, 지나치게 큰 파일만 경고한다.
     */
    const handleUploadImage = (file) => {
        if (!file) return;
        if (!file.type?.startsWith("image/")) {
            notify("이미지 파일만 넣을 수 있습니다", "warning");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        if (file.size > 1024 * 1024) {
            notify(`이미지가 ${Math.ceil(file.size / 1024)}KB 입니다. 서명은 보내는 메일마다 따라붙으니 200KB 이하를 권합니다.`, "warning");
        }

        setUploading(true);
        const reader = new FileReader();
        reader.onload = () => {
            const tag = `<img src="${reader.result}" alt="" style="max-width:100%;" />`;
            const el  = htmlFieldRef.current;
            const at  = el && typeof el.selectionStart === "number" ? el.selectionStart : bodyHtml.length;
            setBodyHtml(bodyHtml.slice(0, at) + tag + bodyHtml.slice(at));
            notify("이미지를 넣었습니다", "success");
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";  // 같은 파일 재선택 허용
        };
        reader.onerror = () => {
            notify("이미지를 읽지 못했습니다", "error");
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        if (!name.trim()) { notify("서명 이름을 입력해주세요", "warning"); return; }
        if (!isHtml && !card.name?.trim()) { notify("이름을 입력해주세요", "warning"); return; }
        setSaving(true);
        try {
            // 방식에 해당하는 본문만 보낸다 — 서버가 이걸로 bodyHtml 을 확정한다.
            const body = isHtml ? { bodyType: "HTML", bodyHtml } : { bodyType: "BASIC", card };
            if (isEdit) {
                await mailApi.updateSignature(initial.id, { name: name.trim(), ...body });
                if (isDefault && !initial.isDefault) {
                    await mailApi.setDefaultSignature(initial.id);
                }
            } else {
                const res = await mailApi.createSignature({ name: name.trim(), ...body, isDefault });
                const created = res?.data ?? res;
                if (isDefault && created?.id) {
                    try { await mailApi.setDefaultSignature(created.id); } catch { /* noop */ }
                }
            }
            notify("서명을 저장했습니다", "success");
            onSaved?.();
        } catch (e) {
            notify(e?.response?.data?.message || "서명 저장 실패", "error");
        } finally { setSaving(false); }
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title={isEdit ? "서명 편집" : "새 서명"}
            subtitle="여기 작성한 내용이 메일 작성/답장 시 본문 끝에 자동으로 들어갑니다."
            maxWidth="md"
            onSave={handleSave}
            saving={saving}
        >
            <Stack spacing={1.5}>
                {/* 기본 서명 설정 — 맨 위. 서명 여러 개를 만드는 사람에게 "어느 것이 자동으로
                    붙는지" 는 이름 다음으로 중요한 정보라, 폼 맨 아래에 있으면 놓친다. */}
                <Box sx={{
                    px: 1.5, py: 0.8, borderRadius: 1.5,
                    bgcolor: isDefault ? "rgba(37,99,235,0.06)" : "#f8fafc",
                    border: `1px solid ${isDefault ? "#bfdbfe" : "#e2e8f0"}`,
                }}>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={<Switch size="small" checked={isDefault}
                                         onChange={(e) => setIsDefault(e.target.checked)} />}
                        label={
                            <Box sx={{ ml: 0.5 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                                    기본 서명으로 설정
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                    새 메일·답장·전달을 열 때 이 서명이 자동으로 들어갑니다.
                                </Typography>
                            </Box>
                        }
                    />
                </Box>

                <TextField
                    label="서명 이름"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    placeholder="예: 영업용, 외부용 등"
                    fullWidth
                    sx={{ "& .MuiInputBase-root": { fontSize: 13 } }}
                />

                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <ToggleButtonGroup
                        size="small" exclusive
                        value={bodyType}
                        onChange={(_, v) => handleChangeType(v)}
                    >
                        <ToggleButton value="BASIC" sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, px: 2 }}>
                            기본
                        </ToggleButton>
                        <ToggleButton value="HTML" sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, px: 2 }}>
                            HTML
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                        {isHtml
                            ? "직접 작성한 HTML 이 그대로 적용됩니다. 스크립트 등 위험한 태그는 저장 시 제거됩니다."
                            : "회사 표준 명함 서명입니다. 아래 값만 채우면 모양은 자동으로 맞춰집니다."}
                    </Typography>
                </Stack>

                {isHtml ? (
                    <>
                        <Box>
                            <Button
                                size="small"
                                startIcon={<ImageRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#475569" }}
                            >
                                {uploading ? "읽는 중…" : "이미지 넣기"}
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => handleUploadImage(e.target.files?.[0])}
                            />
                        </Box>
                        <TextField
                            value={bodyHtml}
                            onChange={(e) => setBodyHtml(e.target.value)}
                            inputRef={htmlFieldRef}
                            multiline
                            minRows={9}
                            maxRows={16}
                            fullWidth
                            placeholder={HTML_PLACEHOLDER}
                            spellCheck={false}
                            InputProps={{
                                sx: {
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                    fontSize: 12.5, lineHeight: 1.6, alignItems: "flex-start",
                                    bgcolor: "#f8fafc",
                                },
                            }}
                        />
                        <PreviewBox html={htmlPreview} />
                    </>
                ) : (
                    <>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", flex: 1 }}>
                                인사정보와 기존 서명에서 채웠습니다. 다른 곳은 그대로 두고 틀린 칸만 고치세요.
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<RefreshRoundedIcon sx={{ fontSize: 15 }} />}
                                disabled={loadingCard}
                                onClick={() => loadCardDefaults(initial?.id)}
                                sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700, color: "#475569" }}
                            >
                                다시 불러오기
                            </Button>
                        </Stack>

                        <Stack direction="row" spacing={1.2}>
                            <CardField label="이름"   value={card.name}     onChange={setField("name")} />
                            <CardField label="부서"   value={card.dept}     onChange={setField("dept")}
                                       suggestions={[hr.dept]} onPick={(v) => setCard(c => ({ ...c, dept: v }))} />
                            <CardField label="직급"   value={card.position} onChange={setField("position")}
                                       suggestions={[hr.position, hr.duty]}
                                       onPick={(v) => setCard(c => ({ ...c, position: v }))} />
                        </Stack>
                        <Stack direction="row" spacing={1.2}>
                            <CardField label="직통 (T.)"  value={card.officePhone} onChange={setField("officePhone")}
                                       help="인사정보에 없는 값이라 직접 확인이 필요합니다" />
                            <CardField label="휴대폰 (M.)" value={card.mobile}      onChange={setField("mobile")} />
                            <CardField label="이메일 (E.)" value={card.email}       onChange={setField("email")} />
                        </Stack>

                        <PreviewBox html={cardPreview} empty="값을 입력하면 여기에 표시됩니다." />
                    </>
                )}
            </Stack>
        </FormModal>
    );
}

/**
 * 명함 값 한 칸.
 *
 * <p>{@code suggestions} 는 인사정보 원본이다. 지금 값과 다를 때만 칩으로 띄운다 —
 * 같은 값을 칩으로 또 보여 주면 화면만 시끄럽고 누를 이유가 없다.
 */
function CardField({ label, value, onChange, suggestions = [], onPick, help }) {
    const alts = [...new Set(suggestions.filter(s => s && s !== value))];
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
                label={label}
                value={value || ""}
                onChange={onChange}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiInputBase-root": { fontSize: 13 } }}
            />
            {help && (
                <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.3, lineHeight: 1.4 }}>
                    {help}
                </Typography>
            )}
            {alts.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.4, flexWrap: "wrap", rowGap: 0.4 }}>
                    {alts.map(a => (
                        <Tooltip key={a} title="인사정보 값으로 바꾸기">
                            <Chip
                                label={a} size="small" variant="outlined"
                                onClick={() => onPick?.(a)}
                                sx={{
                                    height: 19, fontSize: 10.5, fontWeight: 600,
                                    color: "#2563eb", borderColor: "#bfdbfe",
                                    "&:hover": { bgcolor: "#eff6ff" },
                                }}
                            />
                        </Tooltip>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

/**
 * 미리보기 상자 — 두 방식이 같은 틀을 쓴다.
 *
 * <p>흰 바탕에 얹는 이유: 서명은 흰 메일 본문 위에 놓이는 물건이라, 회색 바탕에서 보면
 * 실제와 색이 달라 보인다. 표 테두리가 붙은 명함형일수록 차이가 크다.
 */
function PreviewBox({ html, empty = "내용을 입력하면 여기에 표시됩니다." }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 0.5 }}>
                미리보기
            </Typography>
            <Box
                sx={{
                    p: 1.5, minHeight: 72, maxHeight: 260, overflow: "auto",
                    border: "1px solid #e2e8f0", borderRadius: 1, bgcolor: "#fff",
                    fontSize: 13, color: "#334155",
                    "& img": { maxWidth: "100%", height: "auto" },
                    "& table": { maxWidth: "100%" },
                }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: html
                        ? DOMPurify.sanitize(html, { ADD_ATTR: ["target", "rel"] })
                        : `<span style="color:#94a3b8">${empty}</span>`,
                }}
            />
        </Box>
    );
}
